import {
  CancelIconButton,
  Card,
  Checkbox,
  DataDisplay,
  DeleteIconButton,
  UpdateIconButton,
  Grid,
  Stack,
  ValidationErrors,
  pn,
  useElementBreakpoints,
  Tooltip,
  CANNOT_DELETE_ITEM_IN_USE_MSG,
  formatBoolean,
} from "@compass/core";
import { InlineEditDataModel, Input, Select } from "components";
import {
  Permission,
  PermissionAction,
  TestLevel as TestLevelModel,
  StudentServiceSubTypeEditResource,
  StudentServiceSubTypeViewResource,
  TestLevelViewResource,
  TestLevelEditResource,
} from "generated/openApi";
import { useInlineEditDataModel } from "hooks";
import produce from "immer";
import "moment-timezone";
import { memo } from "react";
import { isAuthorized } from "services";

export const TestLevel = memo(
  (props: {
    rootPermission: Permission;
    testLevel: TestLevelModel;
    setTestLevel: (value: TestLevelModel) => void;
    viewResource: TestLevelViewResource;
    editResource: TestLevelEditResource;
    validationErrors: ValidationErrors | string[];
    cancelCreate: () => void;
  }) => {
    // console.log("rerender");

    const { breakpointsRef, smallFieldBreakpoints, xSmallFieldBreakpoints } = useElementBreakpoints();
    const properties = props.viewResource.properties || [];

    const { canCancel, canEdit, canRemove, cancel, edit, remove, editing, removing } = useInlineEditDataModel({
      dataModel: props.testLevel,
      setDataModel: props.setTestLevel,
      cancelCreate: props.cancelCreate,
    });

    const nameField = () => {
      const propertyName = pn<TestLevelModel>("name");
      const property = properties.find((x) => x.propertyName === propertyName);

      return !editing ? (
        <DataDisplay label={property?.displayName}>{props.testLevel.name}</DataDisplay>
      ) : (
        <Input
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.testLevel.name}
          onChange={(e) =>
            props.setTestLevel(
              produce(props.testLevel, (draft) => {
                draft.name = e.target.value;
              })
            )
          }
        />
      );
    };

    const proficiencyField = () => {
      const propertyName = pn<TestLevelModel>("proficiencyId");
      const property = properties.find((x) => x.propertyName === propertyName);

      return !editing ? (
        <DataDisplay label={property?.displayName}>{props.testLevel.proficiency?.name}</DataDisplay>
      ) : (
        <Select
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.editResource.proficiencies?.find(
            (x) => x.id === props.testLevel.proficiencyId
          )}
          options={props.editResource.proficiencies}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setTestLevel(
              produce(props.testLevel, (draft) => {
                draft.proficiencyId = value?.id;
              })
            )
          }
        />
      );
    };

    return (
      <Card>
        <InlineEditDataModel
          noPadding
          noBorder
          removing={removing}
          actions={
            <>
              {isAuthorized(props.rootPermission, PermissionAction.Update) && canEdit && (
                <UpdateIconButton onClick={edit} size="small" />
              )}
              {isAuthorized(props.rootPermission, PermissionAction.Delete) && canRemove && (
                <DeleteIconButton onClick={remove} size="small" />
              )}
              {canCancel && <CancelIconButton onClick={cancel} size="small" />}
            </>
          }
          content={
            <Grid ref={breakpointsRef} container spacing="sm">
              <Grid item xs={smallFieldBreakpoints}>
                {nameField()}
              </Grid>
              <Grid item xs={smallFieldBreakpoints}>
                {proficiencyField()}
              </Grid>
            </Grid>
          }
        />
      </Card>
    );
  }
);
